<script setup lang="ts">
import { Ref, computed, onMounted, ref } from 'vue'
import { useAppStore } from '@/store/app'
import { useDocument } from '@/composition'
import {
  UIButton,
  UITextMdNormal,
  UIInput,
  UIInputGroup,
  UIDivider,
  UITextLgMedium,
  UITextSmNormal,
  UITextSmMedium,
  UITextXsRegular,
  UISpin,
} from '@gohighlevel/ghl-ui'
import {
  Link02Icon,
  Copy01Icon,
  CheckIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import { useEmitter } from '@/composable/useEmitter'
import CustomModal from '@/components/common/CustomModal.vue'
import { debounce } from 'lodash'

const { saveDocument, publishTemplate, publishingDocument, unpublishTemplate } =
  useDocument()
const generateLinkModal = ref(false)
const showCopyIcons: Ref<boolean> = ref(false)
const appStore = useAppStore()
const emitter = useEmitter()
const link = computed(() => {
  return appStore.docFormPublicLink
})

onMounted(() => {
  emitter.on('open-link-generation-model', () => {
    generateLinkModal.value = true
  })
})

const toggleLinkModal = () => {
  generateLinkModal.value = !generateLinkModal.value
}

const copyLinkReference = () => {
  showCopyIcons.value = true
  navigator.clipboard.writeText(link.value ?? '')
  if (showCopyIcons.value) {
    debounce(() => {
      if (showCopyIcons.value) {
        showCopyIcons.value = false
      }
    }, 1200)()
  }
}

const generateDynamicLinksAndSendDocument = async () => {
  try {
    await saveDocument()
    await publishTemplate()
  } catch (error) {
    console.error(error)
  }
}

const unpublishDocForm = async () => {
  try {
    await unpublishTemplate()
  } catch (error) {
    console.error(error)
  }
}
</script>

<template>
  <CustomModal
    :width="850"
    :show="generateLinkModal"
    :modalType="'primary'"
    :title="$t('templates.shareLinkModal.title2')"
    @on-cancel="toggleLinkModal"
    id="generate-link-modal"
    :cancelActionText="''"
    :successActionText="''"
    class="generate-link-modal"
    :headerIcon="false"
  >
    <div class="flex flex-col mt-[-10px] relative">
      <div
        v-if="publishingDocument"
        class="absolute left-0 top-0 z-[1] w-full h-full flex items-center justify-center rounded-md bg-[rgba(255,255,255,0.91)]"
      >
        <UISpin size="medium" class="z-10" />
      </div>
      <div
        v-if="appStore.document.isPublished"
        class="p-2 mb-6 border border-warning-400 rounded-md bg-warning-50 flex items-center justify-between"
      >
        <div>
          <UITextXsMedium class="items-center flex text-warning-600 font-bold"
            >{{ $t('common.publicDocumentPublished') }}
          </UITextXsMedium>
        </div>
        <div>
          <UIButton
            size="small"
            :loading="publishingDocument"
            :disabled="publishingDocument"
            @click="unpublishDocForm"
            type="primary"
            id="unpublish-from-modal"
          >
            {{ $t('common.unpublish') }}
          </UIButton>
        </div>
      </div>
      <div
        class="flex flex-row bg-primary-50 rounded items-center justify-center px-2 py-3"
      >
        <div class="flex-none w-25">
          <img
            src="/link_generate.png"
            :alt="$t('templates.shareLinkModal.linkIconAlt')"
            aria-hidden="true"
          />
        </div>
        <div class="flex-1 p-4">
          <UITextLgMedium>
            {{ $t('templates.shareLinkModal.subTitle2') }}
          </UITextLgMedium>
          <UIButton
            v-if="!appStore.document.isPublished"
            type="primary"
            id="generate-link-btn"
            class="p-3 my-2"
            @click="generateDynamicLinksAndSendDocument"
            :loading="publishingDocument"
            :disabled="publishingDocument"
            ><Link02Icon class="w-5 h-5 mr-2" />
            {{ $t('proposals.sendLink.generateLink') }}</UIButton
          >
          <div class="flex-1 w-9/12 my-2" v-else>
            <UIInputGroup>
              <UIInput
                readonly
                class="bg-gray-50 cursor-read-only"
                id="recipient-lin-input"
                :value="link"
                :placeholder="$t('templates.shareLinkModal.linkPlaceholder')"
              />
              <UIButton id="recipient-copy-btn" @click="copyLinkReference"
                ><Copy01Icon class="w-4" v-if="!showCopyIcons" />
                <CheckIcon class="w-4 text-emerald-500" v-if="showCopyIcons" />

                <UITextSmNormal
                  class="ml-1"
                  :class="{
                    'text-emerald-500': showCopyIcons,
                  }"
                  >{{
                    !showCopyIcons ? $t('common.copy') : $t('common.copied')
                  }}</UITextSmNormal
                ></UIButton
              >
            </UIInputGroup>
          </div>
          <UITextMdNormal class="text-gray-500">
            {{ $t('templates.shareLinkModal.titleSubText') }}
          </UITextMdNormal>
        </div>
      </div>
      <div class="flex flex-col" v-if="!appStore.document.isPublished">
        <div
          class="recipient-link"
          :class="{ 'is-disabled': !appStore.document.isPublished }"
        >
          <div class="flex my-6">
            <div class="w-6/12">
              <UITextSmMedium>{{ $t('common.embedLink') }}</UITextSmMedium>
              <UITextXsRegular>{{
                $t('common.embedLinkHelperText')
              }}</UITextXsRegular>
            </div>
            <div class="w-6/12">
              <div class="flex-1">
                <UIInputGroup>
                  <UIInput
                    readonly
                    class="bg-gray-50 cursor-read-only"
                    id="recipient-lin-input"
                    :value="link"
                    :placeholder="$t('templates.shareLinkModal.linkPlaceholder')"
                  />
                  <UIButton id="recipient-copy-btn" @click="copyLinkReference"
                    ><Copy01Icon class="w-4" v-if="!showCopyIcons" />
                    <CheckIcon
                      class="w-4 text-emerald-500"
                      v-if="showCopyIcons"
                    />

                    <UITextSmNormal
                      class="ml-1"
                      :class="{
                        'text-emerald-500': showCopyIcons,
                      }"
                      >{{
                        !showCopyIcons ? $t('common.copy') : $t('common.copied')
                      }}</UITextSmNormal
                    ></UIButton
                  >
                </UIInputGroup>
              </div>
            </div>
          </div>
          <UIDivider class="mb-0" />
        </div>
      </div>
    </div>
  </CustomModal>
</template>
